class VoiceToDatabase {
  constructor() {
    this.config = {
      button: () => document.querySelector('[data-module="VoiceToDatabase"]'),
      searchInput: () => document.querySelector('input[name="q"]'),
      searchInputWrapper: () => document.getElementById('searchInputWrapper'),
      recordingSvg: () => document.getElementById('recordingSvg'),
      submitButton: () => document.querySelector('.search-submit-button'),
      soundwave: () => document.getElementById('soundwave'),
      acceptButton: () => document.querySelector('.accept-button'),
      voiceButton: () => document.querySelector('.voice-button')
    };
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.isRecording = false;
  }

  init() {
    const { button, soundwave } = this.config;
    const triggerButton = button();
    const soundwaveElement = soundwave();

    if (triggerButton) {
      triggerButton.addEventListener('click', e => {
        e.preventDefault();
        this.toggleRecording();
      });
    }

    if (soundwaveElement) {
      soundwaveElement.addEventListener('click', e => {
        e.preventDefault();
        if (this.isRecording) {
          this.stopRecording();
        }
      });
    }
  }

  async toggleRecording() {
    if (this.isRecording) {
      this.stopRecording();
    } else {
      await this.startRecording();
    }
  }

  async startRecording() {
    const {
      button,
      searchInputWrapper,
      recordingSvg,
      submitButton,
      acceptButton,
      voiceButton
    } = this.config;
    const triggerButton = button();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = this.getSupportedMimeType();
      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];

      this.mediaRecorder.addEventListener('dataavailable', event => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      });

      this.mediaRecorder.addEventListener('stop', () => {
        this.processAudio();
      });

      this.mediaRecorder.start();
      this.isRecording = true;
      triggerButton.classList.add('recording');
      searchInputWrapper().style.display = 'none';
      acceptButton().style.display = 'inline-block'; // Show the accept button
      voiceButton().style.display = 'none'; // Hide the submit button
      recordingSvg().style.display = 'block';
      submitButton().style.display = 'none'; // Hide the submit button
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  }

  getSupportedMimeType() {
    const types = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/wav'];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return '';
  }

  stopRecording() {
    const {
      button,
      searchInputWrapper,
      recordingSvg,
      submitButton,
      acceptButton,
      voiceButton
    } = this.config;
    const triggerButton = button();

    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      triggerButton.classList.remove('recording');
      searchInputWrapper().style.display = 'block';

      voiceButton().style.display = 'inline-block'; // Show the voice button again
      acceptButton().style.display = 'none'; // Hide the accept button
      recordingSvg().style.display = 'none';
      submitButton().style.display = 'table-cell'; // Show the submit button again
    }
  }

  async processAudio() {
    const audioBlob = new Blob(this.audioChunks);
    this.audioChunks = [];
    await this.processAudioBlob(audioBlob);
  }

  async processAudioBlob(audioBlob) {
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

      const wavBlob = await this.audioBufferToWav(audioBuffer);

      const base64Audio = await this.blobToBase64(wavBlob);

      await this.sendAudioToServer(base64Audio);
    } catch (error) {
      console.error('Error processing audio:', error);
    }
  }

  audioBufferToWav(buffer) {
    const { numberOfChannels, sampleRate } = buffer;
    const length = buffer.length * numberOfChannels * 2;
    const data = new DataView(new ArrayBuffer(44 + length));

    const writeString = (view, offset, string) => {
      for (let i = 0; i < string.length; i += 1) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(data, 0, 'RIFF');
    data.setUint32(4, 36 + length, true);
    writeString(data, 8, 'WAVE');
    writeString(data, 12, 'fmt ');
    data.setUint32(16, 16, true);
    data.setUint16(20, 1, true);
    data.setUint16(22, numberOfChannels, true);
    data.setUint32(24, sampleRate, true);
    data.setUint32(28, sampleRate * numberOfChannels * 2, true);
    data.setUint16(32, numberOfChannels * 2, true);
    data.setUint16(34, 16, true);
    writeString(data, 36, 'data');
    data.setUint32(40, length, true);

    const channelData = [];
    for (let channel = 0; channel < numberOfChannels; channel += 1) {
      channelData.push(buffer.getChannelData(channel));
    }

    let offset = 44;
    for (let i = 0; i < buffer.length; i += 1) {
      for (let channel = 0; channel < numberOfChannels; channel += 1) {
        const sample = Math.max(-1, Math.min(1, channelData[channel][i]));
        data.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }

    return new Blob([data], { type: 'audio/wav' });
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async sendAudioToServer(base64Audio) {
    try {
      const response = await fetch(
        'https://ai-product-search.vercel.app/api/openai/speech-to-text',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audio: base64Audio })
        }
      );
      const data = await response.json();
      if (response.ok) {
        this.updateSearchInput(data.result);
        await this.makeDatabaseQuery(data.result);
      } else {
        console.error('Error:', data.error);
      }
    } catch (error) {
      console.error('Error sending audio to server:', error);
    }
  }

  updateSearchInput(transcribedText) {
    const { searchInput } = this.config;
    const input = searchInput();

    if (transcribedText.toLowerCase().includes('amara.org')) {
      input.value = 'Geen audio opgenomen of onverstaanbaar, probeer opnieuw.';
    } else {
      input.value = transcribedText;
    }
  }

  async makeDatabaseQuery(transcribedText) {
    try {
      if (!transcribedText.toLowerCase().includes('amara.org')) {
        window.location.href = `/producten?aiq=${transcribedText}&omitfilterdata=true`;
      }
    } catch (error) {
      console.error('Error in database query:', error);
    }
  }
}

export default VoiceToDatabase;
